import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { ArrowRight, Ban, CheckCircle2, Clock, Eye, Lock } from 'lucide-react';
import { backofficeApi, extractErrorMessage, formatDateTime } from '@kardume/services';
import { useNotification } from '@kardume/hooks';
import type { BackofficeProfileEditDto, MerchantProfileEditStatus, Tone } from '@kardume/types';
import {
  ActionButton,
  Badge,
  Button,
  Modal,
  PageHeader,
  SearchInput,
  Select,
  StatCard,
  Table,
  TableContainer,
  TextInput,
} from '@kardume/ui';
import { useAsync } from '../utils/useAsync';
import DataState from '../components/DataState';
import ClaimEvidencePanel from '../components/ClaimEvidencePanel';

/**
 * A fila de alterações sensíveis propostas por quem administra a página
 * (card 277, KU-13g; regra §37.11).
 *
 * ## Por que esta tela existe separada da de reivindicações
 *
 * Aprovar uma reivindicação diz **quem administra** uma página. Aprovar uma
 * alteração daqui muda **o que o guia diz** sobre um negócio real - nome,
 * endereço, categoria -, para todo visitante, no instante da decisão. São
 * riscos de ordem diferente, e por isso a permissão é própria
 * (`PROFILE_EDIT_MODERATE`).
 *
 * Sem esta tela a fila acumulava em silêncio: o responsável propunha a mudança,
 * o core enfileirava, e nada nunca era decidido - pior que não ter fila, porque
 * a tela dele dizia "esperando conferência" e ninguém estava conferindo.
 *
 * ## O par antes/depois é o conteúdo da tela
 *
 * A decisão se toma comparando: "Rua das Flores, 120" virando "Av. Brasil,
 * 4500" em outra cidade é uma pergunta; virando "Rua das Flores, 120 - sala 2"
 * é outra. Mostrar só o valor proposto obrigaria o analista a abrir a empresa
 * noutra aba para saber o que está mudando - e é exatamente aí que se aprova no
 * automático.
 *
 * ## O motivo é obrigatório nas duas decisões
 *
 * O servidor recusa decisão sem motivo, e a tela não finge o contrário: o botão
 * de confirmar nasce desabilitado. Recusar sem motivo deixa o responsável sem
 * saber o que corrigir; aprovar sem motivo deixa o histórico sem dizer o que
 * foi conferido.
 */

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;
  margin-bottom: 1.25rem;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const Toolbar = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
  align-items: center;

  > *:first-child {
    flex: 1;
    min-width: 240px;
  }
`;

const Merchant = styled.div`
  min-width: 0;

  strong {
    display: block;
    color: ${(p) => p.theme.textPrimary};
  }
  small {
    display: block;
    color: ${(p) => p.theme.textMuted};
    font-size: 0.78rem;
  }
`;

/**
 * O par antes/depois na linha da tabela.
 *
 * O valor antigo fica riscado e apagado, o novo em destaque: a diferença entre
 * os dois precisa saltar sem que ninguém abra o detalhe, porque é ela que diz
 * se a linha merece uma pergunta.
 */
const Diff = styled.div<{ $wrap?: boolean }>`
  display: flex;
  align-items: ${(p) => (p.$wrap ? 'flex-start' : 'center')};
  gap: 0.45rem;
  flex-wrap: ${(p) => (p.$wrap ? 'wrap' : 'nowrap')};
  min-width: 0;
  font-size: 0.86rem;

  .before {
    color: ${(p) => p.theme.textMuted};
    text-decoration: line-through;
    ${(p) => (p.$wrap ? 'word-break: break-word;' : 'max-width: 220px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;')}
  }
  .after {
    color: ${(p) => p.theme.textPrimary};
    font-weight: 600;
    ${(p) => (p.$wrap ? 'word-break: break-word;' : 'max-width: 240px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;')}
  }
  svg {
    flex-shrink: 0;
    color: ${(p) => p.theme.textMuted};
  }
`;

/* O SLA em destaque quando passa de um dia - o número que diz qual linha da
   fila já virou problema de atendimento. */
const Waiting = styled.span<{ $late: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 0.82rem;
  font-weight: ${(p) => (p.$late ? 700 : 500)};
  color: ${(p) => (p.$late ? p.theme.error : p.theme.textMuted)};
`;

const Detail = styled.div`
  display: grid;
  gap: 0.9rem;

  dl {
    display: grid;
    grid-template-columns: 150px 1fr;
    gap: 0.4rem 1rem;
    margin: 0;
    font-size: 0.88rem;
  }
  dt {
    color: ${(p) => p.theme.textMuted};
  }
  dd {
    margin: 0;
    color: ${(p) => p.theme.textPrimary};
    word-break: break-word;
  }
`;

const ChangeBox = styled.div`
  padding: 0.85rem 0.95rem;
  border: 1px solid ${(p) => p.theme.border};
  border-radius: ${(p) => p.theme.radius};
  background: ${(p) => p.theme.surfaceAlt};
  display: grid;
  gap: 0.4rem;

  .field {
    font-size: 0.75rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: ${(p) => p.theme.textMuted};
  }
`;

const Restricted = styled.p`
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.83rem;
  color: ${(p) => p.theme.textMuted};
`;

const Hint = styled.p`
  margin: 0;
  font-size: 0.88rem;
`;

const ActionsRow = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const STATUS_LABELS: Record<MerchantProfileEditStatus, string> = {
  PENDING: 'Esperando decisão',
  APPLIED: 'Aplicada',
  REJECTED: 'Recusada',
  DISCARDED: 'Descartada',
};

const FILTER_OPTIONS = [
  { value: 'PENDING', label: 'Esperando decisão' },
  { value: '', label: 'Todos os estados' },
  { value: 'APPLIED', label: 'Aplicadas' },
  { value: 'REJECTED', label: 'Recusadas' },
  { value: 'DISCARDED', label: 'Descartadas' },
];

/** Acima disto a alteração é destacada como atrasada. */
const SLA_HOURS = 24;

function statusTone(status: MerchantProfileEditStatus): Tone {
  if (status === 'APPLIED') return 'success';
  if (status === 'REJECTED') return 'danger';
  if (status === 'PENDING') return 'warning';
  return 'secondary';
}

/** Campo apagado é uma decisão, não um vazio: a tela precisa dizer isso. */
const displayValue = (value: string | null): string => (value && value.trim().length > 0 ? value : '(vazio)');

const DECISION_COPY = {
  approve: {
    title: 'Aprovar alteração',
    confirm: 'Aprovar',
    hint: 'O novo valor vai para a página no instante da decisão e passa a valer para todo visitante. Escreva o que você conferiu - é isso que o histórico vai guardar.',
  },
  reject: {
    title: 'Recusar alteração',
    confirm: 'Recusar',
    hint: 'Nada muda na página. O motivo chega a quem propôs: diga o que precisa ser corrigido, e não só que foi recusado.',
  },
} as const;

type DecisionKind = keyof typeof DECISION_COPY;

interface Props {
  /** Permissões efetivas de quem está na tela. */
  permissions: string[];
  isAdmin: boolean;
}

const ProfileEditsPage: React.FC<Props> = ({ permissions, isAdmin }) => {
  const { showNotification } = useNotification();
  const canModerate = isAdmin || permissions.includes('ROLE_BACKOFFICE_PROFILE_EDIT_MODERATE');

  const { data, loading, error, reload } = useAsync(
    () => backofficeApi.listProfileEdits({ size: 100 }),
    [],
  );

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('PENDING');
  const [detail, setDetail] = useState<BackofficeProfileEditDto | null>(null);
  const [deciding, setDeciding] = useState<{ edit: BackofficeProfileEditDto; kind: DecisionKind } | null>(null);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const edits = useMemo(() => data?.content ?? [], [data]);

  const stats = useMemo(() => {
    const pending = edits.filter((e) => e.status === 'PENDING');
    return {
      total: data?.totalElements ?? edits.length,
      pending: pending.length,
      late: pending.filter((e) => e.waitingHours >= SLA_HOURS).length,
      // Uma empresa costuma propor endereço inteiro de uma vez, o que vira sete
      // linhas na fila. O número de empresas diz o tamanho real do trabalho.
      merchants: new Set(pending.map((e) => e.merchantId)).size,
    };
  }, [data, edits]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return edits.filter((edit) => {
      const matchesStatus = !statusFilter || edit.status === statusFilter;
      const matchesTerm =
        !term ||
        [edit.merchantName, edit.merchantCity ?? '', edit.fieldLabel, edit.newValue ?? '', edit.previousValue ?? ''].some(
          (value) => value.toLowerCase().includes(term),
        );
      return matchesStatus && matchesTerm;
    });
  }, [edits, search, statusFilter]);

  const openDecision = (edit: BackofficeProfileEditDto, kind: DecisionKind): void => {
    setDeciding({ edit, kind });
    setReason('');
  };

  const confirmDecision = async (): Promise<void> => {
    if (!deciding) return;
    setSubmitting(true);
    try {
      await backofficeApi.decideProfileEdit(deciding.edit.id, {
        approved: deciding.kind === 'approve',
        reason: reason.trim(),
      });
      showNotification(
        deciding.kind === 'approve' ? 'Alteração aplicada na página.' : 'Alteração recusada.',
        'success',
      );
      setDeciding(null);
      setDetail(null);
      await reload();
    } catch (err) {
      showNotification(extractErrorMessage(err, 'Não foi possível registrar a decisão.'), 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <PageHeader
        title="Moderação de edições"
        subtitle="Nome, endereço e categoria propostos por quem administra a página da empresa"
      />

      <StatsGrid>
        <StatCard label="Alterações" value={String(stats.total)} />
        <StatCard label="Esperando decisão" value={String(stats.pending)} />
        <StatCard label={`Esperando +${SLA_HOURS}h`} value={String(stats.late)} />
        <StatCard label="Empresas na fila" value={String(stats.merchants)} />
      </StatsGrid>

      <Toolbar>
        <SearchInput
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar por empresa, cidade, campo ou valor..."
        />
        <Select
          options={FILTER_OPTIONS}
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        />
      </Toolbar>

      <DataState
        loading={loading}
        error={error}
        onRetry={reload}
        isEmpty={filtered.length === 0}
        emptyTitle="Nada esperando decisão"
        emptyDescription="Nenhuma alteração sensível na fila com este filtro."
      >
        <TableContainer>
          <Table>
            <thead>
              <tr>
                <th>Empresa</th>
                <th>Campo</th>
                <th>De → para</th>
                <th>Estado</th>
                <th>Esperando</th>
                <th style={{ textAlign: 'right' }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((edit) => (
                <tr key={edit.id}>
                  <td>
                    <Merchant>
                      <strong>{edit.merchantName}</strong>
                      <small>{edit.merchantCity || '-'}</small>
                    </Merchant>
                  </td>
                  <td>{edit.fieldLabel}</td>
                  <td>
                    <Diff>
                      <span className="before" title={displayValue(edit.previousValue)}>
                        {displayValue(edit.previousValue)}
                      </span>
                      <ArrowRight size={14} />
                      <span className="after" title={displayValue(edit.newValue)}>
                        {displayValue(edit.newValue)}
                      </span>
                    </Diff>
                  </td>
                  <td>
                    <Badge tone={statusTone(edit.status)}>{STATUS_LABELS[edit.status]}</Badge>
                  </td>
                  <td>
                    <Waiting $late={edit.status === 'PENDING' && edit.waitingHours >= SLA_HOURS}>
                      <Clock size={13} /> {edit.waitingHours}h
                    </Waiting>
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <ActionButton title="Ver detalhe" onClick={() => setDetail(edit)}>
                      <Eye size={16} />
                    </ActionButton>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableContainer>
      </DataState>

      {/* Detalhe: o par antes/depois inteiro (sem corte), o contexto do pedido e
          os anexos da reivindicação que originou a edição - que é onde se
          confere se o endereço proposto bate com o do contrato social. */}
      <Modal
        isOpen={!!detail}
        onClose={() => setDetail(null)}
        title="Alteração proposta"
        width="680px"
      >
        {detail && (
          <Detail>
            <ChangeBox>
              <span className="field">{detail.fieldLabel}</span>
              <Diff $wrap>
                <span className="before">{displayValue(detail.previousValue)}</span>
                <ArrowRight size={15} />
                <span className="after">{displayValue(detail.newValue)}</span>
              </Diff>
            </ChangeBox>

            <dl>
              <dt>Empresa</dt>
              <dd>
                {detail.merchantName}
                {detail.merchantCity ? ` · ${detail.merchantCity}` : ''}
              </dd>
              <dt>Estado</dt>
              <dd>{STATUS_LABELS[detail.status]}</dd>
              <dt>Proposta em</dt>
              <dd>
                {formatDateTime(detail.createdAt)} · esperando {detail.waitingHours}h
              </dd>
              {detail.decidedAt && (
                <>
                  <dt>Decidida em</dt>
                  <dd>{formatDateTime(detail.decidedAt)}</dd>
                </>
              )}
              {detail.decisionReason && (
                <>
                  <dt>Motivo</dt>
                  <dd>{detail.decisionReason}</dd>
                </>
              )}
            </dl>

            <ClaimEvidencePanel claimId={detail.claimId} permissions={permissions} isAdmin={isAdmin} />

            {detail.status === 'PENDING' &&
              (canModerate ? (
                <ActionsRow>
                  <Button variant="primary" onClick={() => openDecision(detail, 'approve')}>
                    <CheckCircle2 size={16} /> Aprovar
                  </Button>
                  <Button variant="outline" onClick={() => openDecision(detail, 'reject')}>
                    <Ban size={16} /> Recusar
                  </Button>
                </ActionsRow>
              ) : (
                <Restricted>
                  <Lock size={14} /> Decidir sobre alterações sensíveis exige a permissão de moderação de
                  edições.
                </Restricted>
              ))}
          </Detail>
        )}
      </Modal>

      <Modal
        isOpen={!!deciding}
        onClose={() => setDeciding(null)}
        title={deciding ? DECISION_COPY[deciding.kind].title : ''}
        width="520px"
        footer={
          <>
            <Button variant="outline" onClick={() => setDeciding(null)}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              // O servidor recusa sem motivo nas duas decisões. Desabilitar
              // aqui evita que o operador descubra isso por um erro depois de
              // clicar - e o motivo é o que explica a decisão a quem propôs.
              disabled={reason.trim().length === 0 || submitting}
              isLoading={submitting}
              onClick={confirmDecision}
            >
              {deciding ? DECISION_COPY[deciding.kind].confirm : ''}
            </Button>
          </>
        }
      >
        {deciding && (
          <Detail>
            <Hint>{DECISION_COPY[deciding.kind].hint}</Hint>
            <ChangeBox>
              <span className="field">{deciding.edit.fieldLabel}</span>
              <Diff $wrap>
                <span className="before">{displayValue(deciding.edit.previousValue)}</span>
                <ArrowRight size={15} />
                <span className="after">{displayValue(deciding.edit.newValue)}</span>
              </Diff>
            </ChangeBox>
            <TextInput
              label="Motivo"
              placeholder="Fica no histórico e é mostrado a quem propôs"
              maxLength={500}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </Detail>
        )}
      </Modal>
    </>
  );
};

export default ProfileEditsPage;
